import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";

import { claimWorkspace, onHosting, runningPackages, workspaceReader } from "../sandboxMount";
import {
  GONE_MESSAGE,
  PORT_MESSAGE,
  READY_MESSAGE,
  previewToken,
  previewWrapper,
} from "./document";
import {
  confine,
  dirOf,
  fileBytes,
  PAGE_MAX_BYTES,
  servePreviewPort,
  WORKSPACE_ROOT,
  type InFlight,
  type WorkspaceReader,
} from "./port";

/** Where a preview reads its workspace; the app's own by default (tests pass theirs). */
export interface PreviewSources {
  reader(workspaceId: string): WorkspaceReader | null;
  /** Takes the workspace up in this tab when no other host has it; null when one does. */
  claim?(workspaceId: string, signal?: AbortSignal): Promise<WorkspaceReader | null>;
  /** Calls back each time this tab becomes the host after standing by; returns the unsubscribe. */
  onHosting?(listener: () => void): () => void;
  /** Remote prefixes the page may load scripts from (our mirror). */
  scripts(): string[];
}

const APP_SOURCES: PreviewSources = {
  reader: workspaceReader,
  claim: claimWorkspace,
  onHosting,
  scripts: () => {
    const packages = runningPackages();
    return packages ? [packages.pyodideIndexURL] : [];
  },
};

export const PREVIEW_HEIGHT = 420;

type Loaded =
  | { state: "loading" }
  | { state: "ready"; srcdoc: string; token: string }
  | { state: "remote" }
  | { state: "error"; message: string };

function errorMessage(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  if (code === "ENOENT") return "This page is no longer in the workspace.";
  if (code === "E2BIG") return "This page is too large to show.";
  return "This page couldn't be read.";
}

/**
 * A workspace page the assistant shows (PF1): the page in a frame sandboxed to an opaque origin,
 * under our CSP, inside our wrapper frame that refuses its navigation (document.ts), reading the
 * workspace over its port from this tab's executor (port.ts). One port at a time: the wrapper
 * relays the page's READY with the load's token and the document's number once per page document
 * (it puts our page back, a few times, when the page's frame leaves it), and each relayed READY
 * closes the previous port and gets a new one, sent back with that number. When the wrapper gives
 * up on the page (GONE) the last port is closed. Try again loads afresh. When another tab or host
 * runs the workspace it says so instead (PF2 routes those); when none does, this tab takes the
 * workspace up (claimWorkspace), as the only tab does after a reload.
 */
export function PreviewFrame({
  workspace,
  path,
  sources = APP_SOURCES,
}: {
  workspace: string;
  path: string;
  sources?: PreviewSources;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  // The latest sources, read when used: a new sources object must not close a spent port (the
  // relay and the bootstrap take one port per page document) nor reload the page.
  const latest = useRef(sources);
  latest.current = sources;
  const [loaded, setLoaded] = useState<Loaded>({ state: "loading" });
  const [attempt, setAttempt] = useState(0);
  const name = path.slice(path.lastIndexOf("/") + 1);

  useEffect(() => {
    let cancelled = false;
    // A claim still waiting when the card goes, or loads afresh, gives its want up.
    const claiming = new AbortController();
    if (confine(WORKSPACE_ROOT, path) !== path) {
      setLoaded({ state: "error", message: "This page couldn't be read." });
      return;
    }
    setLoaded({ state: "loading" });
    // Followed from before the claim: this tab elected while the claim ends, or before "remote"
    // commits, still claims again (after a reload the server keeps the page before it as the host
    // for a while, so even the only tab stands by first).
    let hosted = false;
    let remote = false;
    const unhost = latest.current.onHosting?.(() => {
      hosted = true;
      if (remote) setAttempt((n) => n + 1);
    });
    const show = async () => {
      const { reader, claim } = latest.current;
      // A workspace no tab holds yet (the only tab, after a reload) is taken up by this one.
      const found =
        reader(workspace) ?? (await claim?.(workspace, claiming.signal).catch(() => null)) ?? null;
      if (cancelled) return;
      if (!found) {
        if (hosted) setAttempt((n) => n + 1);
        else {
          remote = true;
          setLoaded({ state: "remote" });
        }
        return;
      }
      unhost?.();
      let bytes: Uint8Array;
      try {
        bytes = await fileBytes(found, path, PAGE_MAX_BYTES);
      } catch (error) {
        if (!cancelled) setLoaded({ state: "error", message: errorMessage(error) });
        return;
      }
      if (cancelled) return;
      const html = new TextDecoder().decode(bytes);
      const token = previewToken();
      try {
        setLoaded({
          state: "ready",
          srcdoc: previewWrapper(html, latest.current.scripts(), token),
          token,
        });
      } catch {
        setLoaded({ state: "error", message: "This page couldn't be shown." });
      }
    };
    void show();
    return () => {
      cancelled = true;
      claiming.abort();
      unhost?.();
    };
  }, [workspace, path, attempt]);

  // Listening from the commit that mounts the frame: its document may post before a passive effect.
  useLayoutEffect(() => {
    if (loaded.state !== "ready") return;
    const { token } = loaded;
    const inFlight: InFlight = { open: 0 };
    let close: (() => void) | null = null;
    const onMessage = (event: MessageEvent) => {
      // The frame's origin is "null": its window and the load's token are the proof of who sent it.
      const target = frame.current?.contentWindow;
      if (!target || event.source !== target) return;
      const data = event.data as { type?: unknown; token?: unknown; doc?: unknown } | null;
      if (data?.token !== token) return;
      if (data.type === GONE_MESSAGE) {
        close?.();
        close = null;
        return;
      }
      if (data.type !== READY_MESSAGE) return;
      // The page document this READY is for replaced the one the previous port served.
      close?.();
      const channel = new MessageChannel();
      close = servePreviewPort(
        channel.port1,
        dirOf(path),
        () => latest.current.reader(workspace),
        inFlight
      );
      target.postMessage({ type: PORT_MESSAGE, token, doc: data.doc }, "*", [channel.port2]);
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      close?.();
    };
  }, [loaded, workspace, path]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return (
    <figure
      data-agent-preview-card={path}
      data-state={loaded.state}
      className="m-0 overflow-hidden rounded-assistant border max-sm:rounded-none max-sm:border-x-0"
    >
      <figcaption className="flex min-w-0 items-center gap-2 border-b px-3 py-1.5 text-xs text-muted-foreground">
        <span className="min-w-0 truncate" title={path}>
          {name}
        </span>
      </figcaption>
      {loaded.state === "ready" ? (
        <iframe
          key={loaded.token}
          ref={frame}
          title={`Preview of ${name}`}
          data-agent-preview={path}
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          srcDoc={loaded.srcdoc}
          className="block w-full border-0 bg-white"
          style={{ height: PREVIEW_HEIGHT }}
        />
      ) : (
        <div
          aria-busy={loaded.state === "loading"}
          className="flex min-h-[60px] items-center gap-2 px-3.5 py-3 text-xs"
        >
          {loaded.state === "loading" && (
            <Loader2
              className="h-4 w-4 text-muted-foreground motion-safe:animate-spin"
              aria-label="Loading"
            />
          )}
          {loaded.state === "remote" && (
            <span data-agent-preview-unavailable className="text-muted-foreground">
              This page shows in the tab running this conversation's sandbox.
            </span>
          )}
          {loaded.state === "error" && (
            <span role="alert" className="text-destructive">
              {loaded.message}
            </span>
          )}
          {loaded.state !== "loading" && (
            <Button variant="outline" size="sm" className="ml-auto max-sm:h-10" onClick={retry}>
              Try again
            </Button>
          )}
        </div>
      )}
    </figure>
  );
}
